import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Two-way binding between a typed object and the URL `?search` string.
 * - Reads initial values from URL on mount.
 * - On `setState`, serializes back to URL (replace, no history pollution).
 * - Defaults are omitted from URL to keep links short.
 *
 * Usage:
 *   const [state, setState] = useUrlState({
 *     search: '',
 *     source: 'all',
 *     limit: 50
 *   });
 */
export function useUrlState<T extends Record<string, string | number | boolean | string[]>>(
  defaults: T
): [T, (next: Partial<T> | ((prev: T) => Partial<T>)) => void] {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const out = { ...defaults } as T;
    (Object.keys(defaults) as Array<keyof T>).forEach(key => {
      const raw = params.getAll(key as string);
      if (raw.length === 0) return;
      const def = defaults[key];
      if (Array.isArray(def)) {
        // multi-value: ?tags=a&tags=b OR ?tags=a,b
        const flat = raw.flatMap(v => v.split(',')).filter(Boolean);
        out[key] = flat as T[keyof T];
      } else if (typeof def === 'number') {
        const n = parseFloat(raw[0]);
        if (Number.isFinite(n)) out[key] = n as T[keyof T];
      } else if (typeof def === 'boolean') {
        out[key] = (raw[0] === '1' || raw[0] === 'true') as T[keyof T];
      } else {
        out[key] = raw[0] as T[keyof T];
      }
    });
    return out;
  }, [params, defaults]);

  const setState = useCallback(
    (next: Partial<T> | ((prev: T) => Partial<T>)) => {
      setParams(prev => {
        const merged = {
          ...state,
          ...(typeof next === 'function' ? next(state) : next)
        } as T;
        const out = new URLSearchParams(prev);
        (Object.keys(defaults) as Array<keyof T>).forEach(key => {
          const v = merged[key];
          const def = defaults[key];
          const isDefault = Array.isArray(def)
            ? Array.isArray(v) && v.length === def.length && v.every((x, i) => x === def[i])
            : v === def;
          out.delete(key as string);
          if (isDefault) return;
          if (Array.isArray(v)) {
            if (v.length > 0) out.set(key as string, v.join(','));
          } else if (v !== '' && v != null) {
            out.set(key as string, String(v));
          }
        });
        return out;
      }, { replace: true });
    },
    [setParams, state, defaults]
  );

  return [state, setState];
}

/** Debounce a value — useful for search inputs that write to URL. */
import { useEffect, useState } from 'react';
export function useDebouncedValue<T>(value: T, delay = 220): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}
